import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  useCreateOnboardingItem,
  useDeleteOnboardingItem,
  useUpdateOnboarding,
  useUpdateOnboardingItem,
} from '../queries';
import type { Onboarding } from '../types';

interface ChecklistProps {
  onboarding: Onboarding;
}

export function OnboardingChecklist({ onboarding }: ChecklistProps) {
  const [newItem, setNewItem] = useState('');
  const addItem = useCreateOnboardingItem();
  const toggleItem = useUpdateOnboardingItem();
  const removeItem = useDeleteOnboardingItem();
  const updateChecklist = useUpdateOnboarding();

  const items = [...onboarding.items].sort((a, b) => a.order - b.order || a.id - b.id);
  const done = items.filter((i) => i.is_done).length;
  const pct = items.length > 0 ? Math.round((done / items.length) * 100) : 0;
  const allDone = items.length > 0 && done === items.length;

  async function submitNew(e: React.FormEvent) {
    e.preventDefault();
    const title = newItem.trim();
    if (!title) return;
    await addItem.mutateAsync({
      onboarding: onboarding.id,
      title,
      order: items.length + 1,
    });
    setNewItem('');
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <CardTitle>Checklist</CardTitle>
          <CardDescription>
            {done}/{items.length} done · {pct}%
          </CardDescription>
        </div>
        {allDone && onboarding.status !== 'completed' ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() =>
              updateChecklist.mutate({
                id: onboarding.id,
                input: { status: 'completed', completed_on: new Date().toISOString().slice(0, 10) },
              })
            }
          >
            Mark complete
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="bg-muted relative h-1.5 w-full overflow-hidden rounded-full">
          <div className="bg-primary h-full transition-all" style={{ width: `${pct}%` }} />
        </div>
        <ul className="space-y-1">
          {items.length === 0 ? (
            <li className="text-muted-foreground py-2 text-sm">No items yet.</li>
          ) : (
            items.map((item) => (
              <li
                key={item.id}
                className="hover:bg-muted/40 flex items-center gap-2 rounded px-2 py-1.5"
              >
                <input
                  id={`ob-item-${item.id}`}
                  type="checkbox"
                  checked={item.is_done}
                  onChange={(e) =>
                    toggleItem.mutate({ id: item.id, input: { is_done: e.target.checked } })
                  }
                  className="size-4"
                  aria-label={`Mark "${item.title}" done`}
                />
                <label
                  htmlFor={`ob-item-${item.id}`}
                  className={cn('flex-1 text-sm', item.is_done && 'text-muted-foreground line-through')}
                >
                  {item.title}
                </label>
                {item.due_date ? (
                  <span className="text-muted-foreground text-xs">
                    Due {new Date(item.due_date).toLocaleDateString()}
                  </span>
                ) : null}
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${item.title}`}
                  onClick={() => removeItem.mutate(item.id)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </li>
            ))
          )}
        </ul>
        <form onSubmit={submitNew} className="flex gap-2">
          <Input
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Add a step…"
            aria-label="New checklist item"
          />
          <Button type="submit" size="sm" disabled={!newItem.trim() || addItem.isPending}>
            <Plus className="size-4" /> Add
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
